export interface UploadTask {
  id: string;
  file?: File;
  name: string;
  size: number;
  type: string;
  context: UploadContext;
  storageKey?: string;
  uploadURL?: string;
  multipartUploadID?: string;
  parts: { partNumber: number; etag: string }[];
  progress: number;
  status:
    | "pending"
    | "getting-url"
    | "uploading"
    | "confirming"
    | "completed"
    | "error"
    | "retry_pending";
  error?: string;
  // rejected marks a task refused by the client pre-flight (byte-size cap or
  // pixel budget) — terminal: the same file can never pass, so Retry is hidden
  // for it and retry()/retryAll() skip it. Persisted with the queue.
  rejected?: boolean;
  retryCount: number;
  nextRetryAt?: number;
}

export type UploadContext =
  | { type: "deliverable"; deliverableId: string; groupId: string }
  | { type: "project"; projectId: string }
  | { type: "replace"; deliverableId: string; groupId: string; assetId: string; propagate: boolean }
  | { type: "knowledge"; category: string; propertyId?: string; sourceId?: string }
  | { type: "content_pool"; apiBaseURL?: string }
  | {
      type: "pool_image";
      baseURL: string;
      slug: string;
      field: string;
      target: string;
      // area is the area-guide scope discriminator — mutually exclusive with slug in
      // practice. When set, the confirm request routes ?area=<area> (AddUploadedAreaImage)
      // instead of ?slug= (property scope) or bare (site-wide). Optional so every other
      // pool_image caller (property/site fields) is unaffected.
      area?: string;
    }
  | { type: "property_image"; baseURL: string }
  | { type: "selection"; galleryId: string }
  | {
      type: "pool_video";
      baseURL: string;
      slug: string;
      field: string;
      target: string;
      // slot distinguishes multiple video placements within the same scope/slug
      // (e.g. hero vs. film). Optional + "" both mean the default/hero slot —
      // confirmUrl omits &slot= entirely so hero's URL stays byte-identical.
      slot?: string;
    };

import {
  fetchWithOOB,
  processOOBSwaps,
  processResponseTriggers,
  getCSRFToken,
} from "./htmx-helpers";
import * as idbFiles from "./idb-files";
import { signalPublishStateChanged } from "./publish-state-signal";
import { readImageDimensions, exceedsPixelBudget, type ImageDimensions } from "./image-dimensions";

class UploadError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const CHUNK_SIZE = 25 * 1024 * 1024; // 25MB chunks for multipart
const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100MB — only truly large files use multipart
const QUEUE_STORAGE_KEY = "studio_upload_queue";

// uploadLimitForContext returns the client-side pre-flight size cap (bytes) for a
// context. The pre-flight check is only a UX nicety (instant rejection); the
// server is the real enforcement boundary.
//
// NO cap is hardcoded here. Every cap is the single source of truth on the server
// (service.UploadCaps / UploadCapForSlot), enforced by each context's
// confirm/handler AND injected into STUDIO_CONFIG.uploadManager (uploadCaps +
// slot-aware poolVideoCaps). The client reads those, so client and server caps
// cannot drift for ANY context. If the config is absent (bootstrap not rendered)
// we fail OPEN — skip the client pre-check and let the server enforce — rather
// than guess a number that could be wrong.
export function uploadLimitForContext(context: UploadContext): number {
  const cfg = (globalThis as any).STUDIO_CONFIG?.uploadManager as
    | { uploadCaps?: Record<string, number>; poolVideoCaps?: Record<string, number> }
    | undefined;
  if (context.type === "pool_video") {
    const cap = cfg?.poolVideoCaps?.[context.slot === "film" ? "film" : ""];
    return typeof cap === "number" && cap > 0 ? cap : Number.POSITIVE_INFINITY;
  }
  const cap = cfg?.uploadCaps?.[context.type];
  return typeof cap === "number" && cap > 0 ? cap : Number.POSITIVE_INFINITY;
}

// imagePreflightApplies reports whether the header-only pixel-budget pre-flight
// (media-04) runs for this file: only image contexts that land in property_images
// (which the server decodes against imaging.MaxDecodePixels) and only for files
// that could be images. Everything else skips the header read entirely.
export function imagePreflightApplies(context: UploadContext, file: Blob): boolean {
  if (context.type !== "property_image" && context.type !== "pool_image") return false;
  return !file.type || file.type.startsWith("image/");
}

// imagePixelBudget returns the server-injected decode budget
// (STUDIO_CONFIG.uploadManager.maxImagePixels = imaging.MaxDecodePixels — the
// single source of truth; never hardcoded here) or 0 when absent/invalid, which
// disables the pre-flight (fail OPEN — the server backstop still marks the row).
export function imagePixelBudget(): number {
  const v = (globalThis as any).STUDIO_CONFIG?.uploadManager?.maxImagePixels;
  return typeof v === "number" && v > 0 ? v : 0;
}

// preflightImageError returns the localized rejection for an over-budget image,
// or null when the upload may proceed (unknown dims / within budget / no budget).
// The message template comes from STUDIO_CONFIG.uploadManager.errors
// .image_too_many_pixels with {width}/{height}/{mp} filled client-side (the max
// MP figure is already baked in server-side from imaging.MaxDecodePixels).
export function preflightImageError(dims: ImageDimensions | null, budget: number): string | null {
  if (!exceedsPixelBudget(dims, budget) || !dims) return null;
  const tmpl: string =
    (globalThis as any).STUDIO_CONFIG?.uploadManager?.errors?.image_too_many_pixels ||
    "Image is {width}×{height} px ({mp} MP) — too large to process.";
  const mp = (Math.round((dims.width * dims.height) / 100_000) / 10).toFixed(1);
  return tmpl
    .replace("{width}", String(dims.width))
    .replace("{height}", String(dims.height))
    .replace("{mp}", mp);
}

// The set of endpoint URLs an upload context can expose. Every field is
// optional because each context type supplies only the subset it needs (e.g.
// only multipart-enabled contexts set initMultipart/presignPart/...); callers
// guard on the specific field they use. confirmUrl is nullable because the
// knowledge context has no confirm step.
export interface UploadEndpoints {
  uploadUrl?: string;
  confirmUrl?: string | null;
  directUploadUrl?: string;
  initMultipart?: string;
  presignPart?: string;
  completeMultipart?: string;
  listParts?: string;
}

// markCompleted is the single completion path for an upload task. Uploads are
// raw fetches (no htmx response processing), so publish-affecting contexts
// dispatch the structural publish-state signal themselves (plan 2 D11).
export function markCompleted(task: UploadTask): void {
  task.status = "completed";
  task.progress = 100;
  idbFiles.deleteFile(task.id).catch(() => {
    /* best-effort */
  });
  if (["pool_image", "pool_video", "property_image"].includes(task.context.type)) {
    signalPublishStateChanged();
  }
}

export function getEndpoints(ctx: UploadContext): UploadEndpoints {
  if (ctx.type === "knowledge") {
    return {
      directUploadUrl: ctx.sourceId
        ? `/admin/knowledge/${ctx.sourceId}/replace`
        : "/admin/knowledge/upload",
      confirmUrl: null,
    };
  }

  if (ctx.type === "content_pool") {
    const base = (ctx.apiBaseURL || "/api") + "/content-pool";
    return {
      uploadUrl: `${base}/upload-url`,
      confirmUrl: `${base}/upload-confirm`,
      initMultipart: `${base}/initiate-multipart`,
      presignPart: `${base}/presign-part`,
      completeMultipart: `${base}/complete-multipart`,
      listParts: `${base}/list-parts`,
    };
  }

  if (ctx.type === "pool_image") {
    // An empty slug is the SITE-LEVEL upload path: the confirm endpoint routes
    // to AddUploadedSiteWide when ?slug= is absent (host portrait + other
    // site-wide fields). A present slug is the property-scoped path. Omit the
    // query param entirely when empty so the server sees no slug at all.
    // area is mutually exclusive with slug and wins when both are present — an
    // area field never also sends ?slug=, but if it did, ?area= is what the
    // server's upload-confirm route actually keys AddUploadedAreaImage on.
    const confirmBase = `${ctx.baseURL}/api/draft/images/upload-confirm`;
    return {
      uploadUrl: `${ctx.baseURL}/api/draft/images/upload-url`,
      confirmUrl: ctx.area
        ? `${confirmBase}?area=${encodeURIComponent(ctx.area)}`
        : ctx.slug
          ? `${confirmBase}?slug=${encodeURIComponent(ctx.slug)}`
          : confirmBase,
    };
  }

  if (ctx.type === "property_image") {
    // Property-scoped canonical-image upload (property hub + website wizard).
    // The property is resolved from the route, so no ?slug= is needed.
    return {
      uploadUrl: `${ctx.baseURL}/images/upload-url`,
      confirmUrl: `${ctx.baseURL}/images/upload-confirm`,
    };
  }

  if (ctx.type === "pool_video") {
    // Scope is symmetric to pool_image (empty slug = homepage → confirm omits
    // ?slug=; a present slug = property scope). UNLIKE pool_image, pool_video is
    // resumable-multipart-enabled: supplying initMultipart/presignPart/
    // completeMultipart/listParts lets the uploader's existing tier logic route
    // files ≥ MULTIPART_THRESHOLD (100 MB) through 25 MB IDB-persistent chunks
    // (a 200 MB hero clip survives reloads); smaller files use the single PUT.
    // The confirm HEAD-validates the staged object.
    const base = `${ctx.baseURL}/api/draft/videos`;
    const confirmBase = `${base}/upload-confirm`;
    const confirmParams: string[] = [];
    if (ctx.slug) confirmParams.push(`slug=${encodeURIComponent(ctx.slug)}`);
    // Only append &slot= for a non-default slot (e.g. "film") — hero/default
    // callers pass no slot, so confirmUrl stays byte-identical to before
    // multi-slot support existed.
    if (ctx.slot) confirmParams.push(`slot=${encodeURIComponent(ctx.slot)}`);
    return {
      uploadUrl: `${base}/upload-url`,
      confirmUrl: confirmParams.length ? `${confirmBase}?${confirmParams.join("&")}` : confirmBase,
      initMultipart: `${base}/initiate-multipart`,
      presignPart: `${base}/presign-part`,
      completeMultipart: `${base}/complete-multipart`,
      listParts: `${base}/list-parts`,
    };
  }

  if (ctx.type === "deliverable") {
    const base = `/api/deliverables/${ctx.deliverableId}/groups/${ctx.groupId}`;
    return {
      uploadUrl: `${base}/upload-url`,
      confirmUrl: `${base}/confirm-upload`,
      initMultipart: `${base}/initiate-multipart`,
      presignPart: `${base}/presign-part`,
      completeMultipart: `${base}/complete-multipart`,
      listParts: `${base}/list-parts`,
    };
  }

  if (ctx.type === "replace") {
    const base = `/api/deliverables/${ctx.deliverableId}/groups/${ctx.groupId}`;
    return {
      uploadUrl: `${base}/assets/${ctx.assetId}/replace-upload-url`,
      confirmUrl: `${base}/assets/${ctx.assetId}/confirm-replace`,
      initMultipart: `${base}/initiate-multipart`,
      presignPart: `${base}/presign-part`,
      completeMultipart: `${base}/complete-multipart`,
      listParts: `${base}/list-parts`,
    };
  }

  if (ctx.type === "selection") {
    // Selection-gallery upload endpoints — request/response shapes are
    // byte-for-byte identical to the deliverable ones (shared DTOs on the
    // server, see internal/api/selection_asset.go), so this branch is a plain
    // base-path swap of the "deliverable" branch above.
    const base = `/api/selection-galleries/${ctx.galleryId}`;
    return {
      uploadUrl: `${base}/upload-url`,
      confirmUrl: `${base}/confirm-upload`,
      initMultipart: `${base}/initiate-multipart`,
      presignPart: `${base}/presign-part`,
      completeMultipart: `${base}/complete-multipart`,
      listParts: `${base}/list-parts`,
    };
  }

  if (ctx.type === "project" && ctx.projectId) {
    const base = `/api/projects/${ctx.projectId}/assets`;
    return {
      uploadUrl: `${base}/upload-url`,
      confirmUrl: `${base}/confirm-upload`,
      initMultipart: `${base}/initiate-multipart`,
      presignPart: `${base}/presign-part`,
      completeMultipart: `${base}/complete-multipart`,
      listParts: `${base}/list-parts`,
    };
  }

  // Unhandled context (e.g. a "project" context with no projectId, or a new
  // type added without a branch here). Fail loudly rather than returning
  // undefined and crashing later on a property access.
  throw new Error(`getEndpoints: unsupported upload context "${ctx.type}"`);
}

function scheduleRetry(task: UploadTask, processQueueFn: () => void) {
  task.retryCount++;
  const delay = Math.min(1000 * Math.pow(2, task.retryCount - 1), 8000);
  task.status = "retry_pending";
  task.nextRetryAt = Date.now() + delay;
  setTimeout(() => processQueueFn(), delay);
}

function isRetryableError(status: number): boolean {
  return status >= 500 || status === 429;
}

export function createUploaderStore(Alpine: any) {
  const activeRequests = new Map<
    string,
    { xhr?: XMLHttpRequest; abortController?: AbortController }
  >();

  const PROGRESS_TIMEOUT_MS = 60_000; // Abort if no upload progress for 60s

  function attachProgressWatchdog(xhr: XMLHttpRequest): { clear: () => void; timedOut: boolean } {
    const state = { timedOut: false };
    let lastProgress = Date.now();
    // Once the browser has sent every byte, the watchdog's job is done: the
    // request is now waiting for the server's response, which is NOT a stall.
    // This matters for the media-proxy, where the app buffers each part and
    // then forwards it to R2 — the browser's upload finishes before the object
    // reaches R2, so upload-progress events legitimately stop while we wait.
    let uploadComplete = false;
    const onProgress = (e: ProgressEvent) => {
      lastProgress = Date.now();
      if (e.lengthComputable && e.loaded >= e.total) uploadComplete = true;
    };
    const onUploadDone = () => {
      uploadComplete = true;
    };
    xhr.upload.addEventListener("progress", onProgress);
    xhr.upload.addEventListener("load", onUploadDone);
    const interval = window.setInterval(() => {
      if (!uploadComplete && Date.now() - lastProgress > PROGRESS_TIMEOUT_MS) {
        state.timedOut = true;
        try {
          xhr.abort();
        } catch {
          /* ignore */
        }
      }
    }, 5_000);
    return {
      clear: () => {
        window.clearInterval(interval);
        xhr.upload.removeEventListener("progress", onProgress);
        xhr.upload.removeEventListener("load", onUploadDone);
      },
      get timedOut() {
        return state.timedOut;
      },
    };
  }

  // umError resolves a localized uploader error message (server-injected via
  // STUDIO_CONFIG.uploadManager.errors, see templates/studio_config.templ),
  // falling back to English, and substitutes {n}/{status} placeholders.
  function umError(key: string, fallback: string, vars?: Record<string, string | number>): string {
    let s: string = (window as any).STUDIO_CONFIG?.uploadManager?.errors?.[key] || fallback;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
    }
    return s;
  }

  return {
    queue: [] as UploadTask[],
    processing: [] as string[],
    maxConcurrent: 3,
    isDrawerOpen: false,
    completedLabel: (window as any).STUDIO_CONFIG?.uploadManager?.completed || "Completed",

    async init() {
      this.loadQueue();

      // Hydrate file references from IDB for any task that lost its `file`
      // due to a page reload mid-upload.
      if (await idbFiles.isAvailable()) {
        const ACTIVE_OR_PENDING = [
          "pending",
          "getting-url",
          "uploading",
          "confirming",
          "retry_pending",
        ];
        for (const task of this.queue) {
          if (!task.file && ACTIVE_OR_PENDING.includes(task.status)) {
            try {
              const file = await idbFiles.getFile(task.id);
              if (file) {
                task.file = file;
              }
            } catch (e) {
              console.warn("[uploader] IDB hydrate failed for task", task.id, e);
            }
          }
        }

        // Garbage-collect IDB entries for tasks that no longer exist in the queue
        // (e.g., crashed sessions, manual localStorage clears).
        try {
          await idbFiles.pruneExcept(this.queue.map((t) => t.id));
        } catch (e) {
          console.warn("[uploader] IDB prune failed", e);
        }
      }

      // Mark any task still missing a file (IDB unavailable, or file genuinely
      // lost) as needs-reattach via `error` status with a clear message.
      // Reset transient in-flight states (uploading/getting-url/confirming) so
      // the queue resumes on the next processQueue tick.
      const ACTIVE_OR_PENDING = [
        "pending",
        "getting-url",
        "uploading",
        "confirming",
        "retry_pending",
      ];
      for (const task of this.queue) {
        if (!task.file && ACTIVE_OR_PENDING.includes(task.status)) {
          task.status = "error";
          task.error =
            (window as any).STUDIO_CONFIG?.uploadManager?.file_lost ||
            "Upload interrupted — please re-add this file.";
        }
        if (
          task.file &&
          (task.status === "uploading" ||
            task.status === "getting-url" ||
            task.status === "confirming")
        ) {
          task.status = "pending";
        }
      }

      this.saveQueue();

      if (this.queue.some((t) => t.status === "pending" || t.status === "retry_pending")) {
        this.processQueue();
      }
    },

    saveQueue() {
      const serializableQueue = this.queue.map((t) => {
        const { file, ...rest } = t;
        return rest;
      });
      localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(serializableQueue));
    },

    loadQueue() {
      const stored = localStorage.getItem(QUEUE_STORAGE_KEY);
      if (stored) {
        try {
          this.queue = JSON.parse(stored);
        } catch (e) {
          console.error("Failed to parse stored upload queue", e);
        }
      }
    },

    isAnyUploadActive(): boolean {
      const ACTIVE = ["pending", "getting-url", "uploading", "confirming", "retry_pending"];
      return this.queue.some((t) => ACTIVE.includes(t.status));
    },

    async addFiles(files: FileList | File[], context: UploadContext) {
      const rawFiles = Array.from(files);
      const limit = uploadLimitForContext(context);
      const errorLabel =
        (window as any).STUDIO_CONFIG?.uploadManager?.file_too_large || "File too large";

      // Pixel-budget pre-flight (media-04): header-only dimension read for image
      // contexts, in parallel, BEFORE building tasks. Promise.all keeps results
      // index-aligned with rawFiles, so task order stays identical to input order.
      // Byte-size rejection keeps precedence (no header read for those files).
      // Any null (unknown format / read error / no budget) fails OPEN.
      const pixelBudget = imagePixelBudget();
      const pixelErrors: (string | null)[] =
        pixelBudget > 0 && rawFiles.some((f) => imagePreflightApplies(context, f))
          ? await Promise.all(
              rawFiles.map(async (f) => {
                if (f.size > limit || !imagePreflightApplies(context, f)) return null;
                return preflightImageError(await readImageDimensions(f), pixelBudget);
              }),
            )
          : rawFiles.map(() => null);

      const newTasks: UploadTask[] = rawFiles.map((file, idx) => {
        const isTooLarge = file.size > limit;
        const pixelError = isTooLarge ? null : pixelErrors[idx];
        const rejected = isTooLarge || pixelError !== null;
        return {
          id: Math.random().toString(36).substring(2, 11),
          file,
          name: file.name,
          size: file.size,
          type: file.type || "application/octet-stream",
          context,
          progress: 0,
          status: rejected ? ("error" as const) : ("pending" as const),
          error: isTooLarge ? errorLabel : (pixelError ?? undefined),
          rejected: rejected || undefined,
          retryCount: 0,
          parts: [],
        };
      });

      this.queue = [...this.queue, ...newTasks];

      // A pre-flight-rejected task (too many bytes or pixels) is marked "error"
      // above WITHOUT ever entering the upload lifecycle, so the per-task failure events
      // dispatched during upload (below) never fire for it. Notify listeners now
      // so a caller's "uploading" UI clears instead of hanging forever — e.g. the
      // CMS video widget's spinner, which waits for pool-video-upload-failed.
      for (const t of newTasks) {
        if (t.status !== "error") continue;
        if (context.type === "pool_video") {
          window.dispatchEvent(
            new CustomEvent("pool-video-upload-failed", {
              detail: { field: context.field, target: context.target, error: t.error },
            }),
          );
        } else if (context.type === "pool_image") {
          window.dispatchEvent(
            new CustomEvent("pool-image-upload-failed", {
              detail: { field: context.field, target: context.target, error: t.error },
            }),
          );
        }
      }

      // Best-effort: persist files to IDB so they survive a page reload.
      // Failures (quota, private mode, no IDB support) just disable resume;
      // uploads still work in-session.
      const idbAvailable = await idbFiles.isAvailable();
      if (idbAvailable) {
        await Promise.all(
          newTasks.map(async (t) => {
            if (t.file && t.status !== "error") {
              try {
                await idbFiles.putFile(t.id, t.file);
              } catch (e) {
                console.warn("[uploader] IDB put failed for task", t.id, e);
              }
            }
          }),
        );
      }

      this.isDrawerOpen = true;
      this.saveQueue();
      this.processQueue();
    },

    async reattachFile(taskID: string, file: File) {
      const task = this.queue.find((t) => t.id === taskID);
      if (task) {
        task.file = file;
        task.status = "pending";
        task.error = undefined;
        idbFiles.putFile(taskID, file).catch(() => {
          /* best-effort */
        });
        this.processQueue();
      }
    },

    async processQueue() {
      if (this.processing.length >= this.maxConcurrent) return;

      const now = Date.now();
      const nextTask = this.queue.find(
        (t) =>
          t.file &&
          // Exclude tasks already in flight. uploadFile is async and only flips
          // status to "uploading" later, so without this guard the synchronous
          // pool-filling recursion below re-selects the SAME still-"pending" task
          // up to maxConcurrent times — starting one file's upload 3× at once.
          // Benign for single-PUT (idempotent re-PUT) but CORRUPTS multipart:
          // each concurrent run calls initiate-multipart (racing past the
          // `!multipartUploadID` guard) → multiple upload IDs, parts scattered
          // across them + a shared parts array → CompleteMultipartUpload fails
          // with InvalidPart / EntityTooSmall.
          !this.processing.includes(t.id) &&
          (t.status === "pending" ||
            (t.status === "retry_pending" && t.nextRetryAt && now >= t.nextRetryAt)),
      );
      if (!nextTask) return;

      if (nextTask.status === "retry_pending") {
        nextTask.status = "pending";
      }

      this.processing.push(nextTask.id);
      this.saveQueue();

      this.uploadFile(nextTask).finally(() => {
        this.processing = this.processing.filter((id) => id !== nextTask.id);
        this.saveQueue();
        this.processQueue();
      });

      this.processQueue();
    },

    async uploadFile(task: UploadTask) {
      try {
        if (!task.file)
          throw new Error(umError("file_missing", "File missing — please re-attach the file."));

        if (task.context.type === "knowledge") {
          await this.uploadDirect(task);
          markCompleted(task);
          this.saveQueue();
          return;
        }

        const endpoints = getEndpoints(task.context);

        // Determine upload tier: single PUT vs multipart
        if (task.size >= MULTIPART_THRESHOLD && endpoints.initMultipart) {
          if (!task.multipartUploadID) {
            const resp = await fetch(endpoints.initMultipart, {
              method: "POST",
              headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
              body: JSON.stringify({ filename: task.name, content_type: task.type }),
            });
            if (!resp.ok)
              throw new UploadError(
                umError("init_multipart", "Could not start the upload"),
                resp.status,
              );
            const info = await resp.json();
            task.multipartUploadID = info.upload_id;
            task.storageKey = info.storage_key;
          }
          await this.uploadMultipart(task);
        } else {
          await this.uploadSingle(task);
        }

        // Confirm upload
        task.status = "confirming";
        const confirmBody: any = {
          storage_key: task.storageKey,
          filename: task.name,
          content_type: task.type,
          size_bytes: task.size,
        };
        if (task.context.type === "replace") confirmBody.propagate = task.context.propagate;
        // "selection" needs no extra fields: domain.SelectionAssetInput (the
        // confirm-upload payload SelectionHandler.ConfirmUpload binds against)
        // is storage_key/filename/content_type/size_bytes only — identical to
        // the generic body above — so no narrowing arm is needed here.

        if (!endpoints.confirmUrl) throw new Error("Confirm URL missing for upload context");
        const resp = await fetch(endpoints.confirmUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "HX-Request": "true",
            "X-CSRF-Token": getCSRFToken(),
          },
          body: JSON.stringify(confirmBody),
        });

        if (!resp.ok)
          throw new UploadError(
            umError("confirm", "Upload confirmation failed (status {status})", {
              status: resp.status,
            }),
            resp.status,
          );

        if (task.context.type === "pool_image") {
          // The CMS image picker can't await this fire-and-forget upload, so
          // notify it via a window event carrying the new pool image.
          try {
            const body = await resp.json();
            window.dispatchEvent(
              new CustomEvent("pool-image-uploaded", {
                // slug = the uploaded image's property scope ("" = site-wide). Carried
                // so EVERY picker on the page (not just the uploader) can add the new
                // image to its pool with the right property_slug and show it without a
                // reload — its own visiblePool filter decides visibility.
                detail: {
                  field: task.context.field,
                  target: task.context.target,
                  id: body.id,
                  url: body.url,
                  slug: task.context.slug,
                  area: task.context.area || "",
                },
              }),
            );
          } catch {
            /* non-fatal: the image is saved server-side; the picker just won't auto-add it */
          }
        } else if (task.context.type === "property_image") {
          // Grid/panel self-refreshes via hx-trigger="property-images-updated from:body";
          // the re-fetch rebuilds the fullscreen curation modal's candidate set fresh so a
          // subsequent reconcile can never archive this just-uploaded canonical image.
          document.body.dispatchEvent(
            new CustomEvent("property-images-updated", { bubbles: true }),
          );
        } else if (task.context.type === "pool_video") {
          try {
            const body = await resp.json();
            window.dispatchEvent(
              new CustomEvent("pool-video-uploaded", {
                detail: {
                  field: task.context.field,
                  target: task.context.target,
                  id: body.id,
                  slug: task.context.slug,
                },
              }),
            );
          } catch {
            /* non-fatal: row is saved server-side; the widget just won't auto-advance */
          }
        } else {
          const html = await resp.text();
          if (html.trim()) processOOBSwaps(html);
          processResponseTriggers(resp);
        }

        markCompleted(task);
      } catch (err: any) {
        if (err.message === "AbortError" || err.name === "AbortError") return;
        console.error(`Upload failed for ${task.name}:`, err);

        const status = err instanceof UploadError ? err.status : 0;

        if ((isRetryableError(status) || status === 0) && task.retryCount < 3) {
          scheduleRetry(task, () => this.processQueue());
        } else {
          task.status = "error";
          task.error = err.message;
          // Terminal failure (non-retryable, or retries exhausted). For
          // fire-and-forget pool_image/pool_video uploads the picker is still
          // showing a spinner waiting on an uploaded event that will never
          // come — notify it so it can decrement its pending count and clear
          // `uploading`. Guarded so other contexts are unaffected.
          if (task.context.type === "pool_image") {
            window.dispatchEvent(
              new CustomEvent("pool-image-upload-failed", {
                detail: { field: task.context.field, target: task.context.target },
              }),
            );
          }
          if (task.context.type === "pool_video") {
            window.dispatchEvent(
              new CustomEvent("pool-video-upload-failed", {
                detail: { field: task.context.field, target: task.context.target },
              }),
            );
          }
        }
      }
      this.saveQueue();
    },

    async uploadSingle(task: UploadTask) {
      const endpoints = getEndpoints(task.context);

      // Always fetch fresh presigned URL (JIT)
      task.status = "getting-url";
      const resp = await fetch(endpoints.uploadUrl!, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
        body: JSON.stringify({ filename: task.name, content_type: task.type }),
      });
      if (!resp.ok)
        throw new UploadError(umError("upload_url", "Failed to get upload URL"), resp.status);
      const info = await resp.json();
      task.uploadURL = info.upload_url || info.url;
      task.storageKey = info.storage_key;

      task.status = "uploading";

      return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        activeRequests.set(task.id, { xhr });
        const watchdog = attachProgressWatchdog(xhr);
        xhr.open("PUT", task.uploadURL!);
        xhr.setRequestHeader("Content-Type", task.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) task.progress = Math.round((e.loaded / e.total) * 100);
        };
        xhr.onload = () => {
          watchdog.clear();
          activeRequests.delete(task.id);
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else
            reject(
              new UploadError(
                umError("status", "Upload failed (status {status})", { status: xhr.status }),
                xhr.status,
              ),
            );
        };
        xhr.onerror = () => {
          watchdog.clear();
          activeRequests.delete(task.id);
          reject(new UploadError(umError("network", "Upload failed (network)"), 0));
        };
        xhr.onabort = () => {
          watchdog.clear();
          activeRequests.delete(task.id);
          if (watchdog.timedOut) {
            reject(new UploadError(umError("stalled", "Upload stalled (no progress)"), 0));
          } else {
            reject(new Error("AbortError"));
          }
        };
        xhr.send(task.file);
      });
    },

    async uploadDirect(task: UploadTask) {
      const endpoints = getEndpoints(task.context);
      if (!endpoints.directUploadUrl) throw new Error("Direct upload URL missing");
      // Capture into a const so the narrowed (non-undefined) type survives into
      // the Promise executor closure below.
      const directUploadUrl = endpoints.directUploadUrl;

      task.status = "uploading";
      const formData = new FormData();
      formData.append("file", task.file!);
      formData.append("category", (task.context as any).category);
      if ((task.context as any).propertyId) {
        formData.append("property_id", (task.context as any).propertyId);
      }

      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        activeRequests.set(task.id, { xhr });
        const watchdog = attachProgressWatchdog(xhr);
        xhr.open("POST", directUploadUrl);
        xhr.setRequestHeader("X-CSRF-Token", getCSRFToken());
        xhr.setRequestHeader("HX-Request", "true");

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) task.progress = Math.round((e.loaded / e.total) * 100);
        };

        xhr.onload = () => {
          watchdog.clear();
          activeRequests.delete(task.id);
          if (xhr.status >= 200 && xhr.status < 300) {
            const triggerVal = xhr.getResponseHeader("HX-Trigger");
            if (triggerVal) {
              try {
                const triggers = JSON.parse(triggerVal);
                Object.entries(triggers).forEach(([name, detail]) => {
                  document.body.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
                });
              } catch (e) {
                document.body.dispatchEvent(new CustomEvent(triggerVal, { bubbles: true }));
              }
            }
            resolve(xhr.response);
          } else {
            reject(
              new Error(
                umError("status", "Upload failed (status {status})", { status: xhr.status }),
              ),
            );
          }
        };

        xhr.onerror = () => {
          watchdog.clear();
          activeRequests.delete(task.id);
          reject(new Error(umError("network", "Upload failed — network error")));
        };
        xhr.onabort = () => {
          watchdog.clear();
          activeRequests.delete(task.id);
          if (watchdog.timedOut) {
            reject(new UploadError(umError("stalled", "Upload stalled (no progress)"), 0));
          } else {
            reject(new Error("AbortError"));
          }
        };

        xhr.send(formData);
      });
    },

    async uploadMultipart(task: UploadTask) {
      const endpoints = getEndpoints(task.context);
      task.status = "uploading";
      const totalParts = Math.ceil(task.size / CHUNK_SIZE);

      // Reconcile with R2 if we have cached parts (resumability)
      if (task.parts.length > 0 && endpoints.listParts) {
        try {
          const resp = await fetch(endpoints.listParts, {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
            body: JSON.stringify({
              storage_key: task.storageKey,
              upload_id: task.multipartUploadID,
            }),
          });
          if (resp.ok) {
            const serverParts: { part_number: number; etag: string }[] = await resp.json();
            task.parts = task.parts.filter((local) =>
              serverParts.some((s) => s.part_number === local.partNumber && s.etag === local.etag),
            );
          } else {
            task.parts = [];
          }
        } catch {
          task.parts = [];
        }
      }

      const partsToUpload: number[] = [];
      for (let i = 1; i <= totalParts; i++) {
        if (!task.parts.find((p) => p.partNumber === i)) {
          partsToUpload.push(i);
        }
      }

      const uploadPart = async (partNumber: number) => {
        const start = (partNumber - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, task.size);
        const chunk = task.file!.slice(start, end);

        const resp = await fetch(endpoints.presignPart!, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({
            storage_key: task.storageKey,
            upload_id: task.multipartUploadID,
            part_number: partNumber,
          }),
        });
        if (!resp.ok)
          throw new UploadError(
            umError("presign_part", "Could not prepare part {n}", { n: partNumber }),
            resp.status,
          );
        const { upload_url } = await resp.json();

        const etag = await new Promise<string>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          activeRequests.set(`${task.id}-${partNumber}`, { xhr });
          const watchdog = attachProgressWatchdog(xhr);
          xhr.open("PUT", upload_url);
          xhr.onload = () => {
            watchdog.clear();
            activeRequests.delete(`${task.id}-${partNumber}`);
            if (xhr.status >= 200 && xhr.status < 300) {
              const etag = xhr.getResponseHeader("ETag");
              if (etag) resolve(etag);
              else
                reject(
                  new UploadError(
                    umError("missing_etag", "Missing ETag in server response"),
                    xhr.status,
                  ),
                );
            } else
              reject(
                new UploadError(
                  umError("part_failed", "Part {n} failed", { n: partNumber }),
                  xhr.status,
                ),
              );
          };
          xhr.onerror = () => {
            watchdog.clear();
            reject(
              new UploadError(umError("part_failed", "Part {n} failed", { n: partNumber }), 0),
            );
          };
          xhr.onabort = () => {
            watchdog.clear();
            activeRequests.delete(`${task.id}-${partNumber}`);
            if (watchdog.timedOut) {
              reject(
                new UploadError(
                  umError("part_stalled", "Part {n} stalled (no progress)", { n: partNumber }),
                  0,
                ),
              );
            } else {
              reject(new Error("AbortError"));
            }
          };
          xhr.send(chunk);
        });

        task.parts.push({ partNumber, etag });
        task.progress = Math.round((task.parts.length / totalParts) * 100);
        this.saveQueue();
      };

      const concurrencyLimit = 3;
      const pool = new Set<Promise<void>>();
      for (const partNum of partsToUpload) {
        if (pool.size >= concurrencyLimit) await Promise.race(pool);
        const promise = uploadPart(partNum).finally(() => pool.delete(promise));
        pool.add(promise);
      }
      await Promise.all(pool);

      const completeResp = await fetch(endpoints.completeMultipart!, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
        body: JSON.stringify({
          storage_key: task.storageKey,
          upload_id: task.multipartUploadID,
          parts: task.parts
            .sort((a, b) => a.partNumber - b.partNumber)
            .map((p) => ({ PartNumber: p.partNumber, ETag: p.etag })),
        }),
      });
      if (!completeResp.ok)
        throw new UploadError(
          umError("complete", "Could not finish the upload"),
          completeResp.status,
        );
    },

    cancelTask(taskID: string) {
      const req = activeRequests.get(taskID);
      if (req) {
        if (req.xhr) req.xhr.abort();
        if (req.abortController) req.abortController.abort();
        activeRequests.delete(taskID);
      }
      // For multipart chunks
      for (const [key, val] of activeRequests.entries()) {
        if (key.startsWith(`${taskID}-`)) {
          if (val.xhr) val.xhr.abort();
          activeRequests.delete(key);
        }
      }
      idbFiles.deleteFile(taskID).catch(() => {
        /* best-effort */
      });
      this.queue = this.queue.filter((t) => t.id !== taskID);
      if (this.queue.length === 0) this.isDrawerOpen = false;
      this.saveQueue();
    },

    retryAll() {
      this.queue.forEach((t) => {
        // Pre-flight rejections are terminal (same file can never pass).
        if (t.status === "error" && !t.rejected) {
          t.status = "pending";
          t.retryCount = 0;
          t.error = undefined;
        }
      });
      this.processQueue();
    },

    cancelAll() {
      this.queue.forEach((task) => {
        if (task.status !== "completed") this.cancelTask(task.id);
      });
      this.queue = this.queue.filter((t) => t.status === "completed");
      if (this.queue.length === 0) this.isDrawerOpen = false;
      this.saveQueue();
    },

    retry(taskID: string) {
      const task = this.queue.find((t) => t.id === taskID);
      // A pre-flight rejection (too many bytes/pixels) can never succeed on
      // retry — re-queuing it would bypass the pre-flight and upload anyway.
      if (task && !task.rejected) {
        task.status = "pending";
        task.retryCount = 0;
        task.error = undefined;
        this.processQueue();
      }
    },

    clearCompleted() {
      const completedIds = this.queue.filter((t) => t.status === "completed").map((t) => t.id);
      Promise.all(completedIds.map((id) => idbFiles.deleteFile(id).catch(() => {}))).catch(
        () => {},
      );
      this.queue = this.queue.filter((t) => t.status !== "completed");
      if (this.queue.length === 0) this.isDrawerOpen = false;
      this.saveQueue();
    },

    toggleDrawer() {
      this.isDrawerOpen = !this.isDrawerOpen;
    },
  };
}
